// Imports ------------------------------------------------------------------->
import {Component, attr, handler} from "../Component";
import {element} from "../CustomElement";
import {i18n, ll} from "../../i18n/I18n";
import {breakLines} from "../../utils/coupons";
import * as ecomUtil from "../../utils/ecom";

import {LazyImage} from "../flipp-lazy-image/LazyImage";
import {ButtonLink} from "../flipp-link/Link";
import {Dialog} from "../flipp-dialog/Dialog";
import {ClipEcomItemButton} from "../flipp-clip-button/ClipEcomItemButton";
import {ItemDialog} from "../flipp-item-dialog/ItemDialog";

import {inject} from "../../services/di";
import {ConfigService} from "../../services/ConfigService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {EcomService, EcomItem} from "../../services/EcomService";
import { LocationService } from "../../services/LocationService";
import { isCrawler } from "../../utils/crawler";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

export type EcomItemTileContext = "buy" | "listing";

interface EcomItemTileAttributes extends JSX.HTMLProps {
	item: EcomItem;

	secondary?: boolean;
	context?: EcomItemTileContext;
}

@element("flipp-ecom-item-tile")
export class EcomItemTile extends Component<EcomItemTileAttributes, {}> {
	@handler item: EcomItem;

	@attr secondary: boolean;
	@attr context: EcomItemTileContext = "listing";

	@inject ecomService: EcomService;
	@inject analytics: AnalyticsService;
	@inject config: ConfigService;
	@inject location: LocationService;

	refs: {
		link: ButtonLink;
	};

	private _ecomDialog: Dialog = null;

	onRender() {
		const link = this.refs.link as ButtonLink;
		link.onclick = () => {
			const dialog = this._getDialog();
			if (!dialog.open && !this.item.expired) {
				dialog.showModal();
			}
		};
	}

	render() {
		return this._render(false, this.item);
	}

	private _render = (loading: boolean, item: EcomItem) => {
		const nameLines = breakLines(item.name, 23, 2);
		const EcomItemPopLink = item.expired ? DivContainer : ButtonLink;
		return <div class="ecom-item-container" classList={{expired: item.expired}} data-context={this.context}>
			<EcomItemPopLink ref="link" class="ecom-item">
				<div class="content-wrapper">
					<div class="image">
						<LazyImage alt={this.getAlt(item)} dataSrc={ecomUtil.thumbnail(item)} />
					</div>

					<div class="info">
						{isCrawler() ? <h3>{this.getH3(item)}</h3> : null }
						{item.originalPrice && item.originalPrice !== item.currentPrice ?
							<div class="original-price-info" flex-vertical>
								<div flex-horizontal>
									<flipp-price value={`${item.currentPrice}`} />
									<flipp-price class="original-price" value={`${item.originalPrice}`} />
								</div>
								<div class="discount" flex-horizontal>
									<span>{i18n("save")}</span>
									<flipp-price value={`${Number(item.originalPrice) - Number(item.currentPrice)}`}/>
								</div>
							</div>
						: item.currentPrice ?
							<flipp-price value={`${item.currentPrice}`} />
						:
							null
						}
						{/* This buffer line is used to make sure that the price div is always located at the edge of the tile */}
						<div class="name-lines">
							{ nameLines.map((line) =>
								<p class="name-text">{line}</p>
							)}
							{ nameLines.length === 1 ? <p class="name-text-buffer"></p> : null }
						</div>
						{/*<div class="name">{item.name}</div>*/}
					</div>

					{this.context === "buy" ?
						<div class="ttm-area">
							<div class="at-merchant">{item.merchant ? i18n("at_merchant_name", { merchantName: item.merchant }) : null}</div>
						</div>
					: null}
				</div>
			</EcomItemPopLink>

			{/* Awkward position for the TTM button, but FF does not allow a link to be within a button. The EcomItemTile
			* *needs* to be a link since we don't have deeplinks for ecom items yet and the TTM button *needs* to be a
			* link so the user can ctrl-click it, so we're stuck doing this dance for now. */}
			{this.context === "buy" && !item.expired ?
				<a class="ttm-btn" target="_blank" onClick={this._handleBuyClick} href={item.url}>{i18n("buy_online")}</a>
			: item.expired ?
				<div class="ttm-btn">{i18n("buy_online")}</div>
			: null}

			{item.merchantLogo && this.context === "listing" ?
				<LazyImage class="merchant-logo" fit contain alt={item.merchant}
					dataSrc={item.merchantLogo}/> : null}

			{!item.expired ?
				<div class="online-badge">{i18n("online")}</div>
			:
				<div class="expired-badge">{i18n("expired")}</div>
			}

		</div>;
	}

	private _getDialog = () => {
		if (!this._ecomDialog) {
			flipp.setVirtualDOM(false);
			const ecomItem = this.item;
			this._ecomDialog = (
				<Dialog class="ecom-item-pop" onClose={this.handleDialogClose_} transient>
					<EcomItemDialogContent itemId={ecomItem.id} handleBuyClick={this._handleBuyClick}/>
				</Dialog>) as Dialog;
		}
		return this._ecomDialog;
	}

	private handleDialogClose_ = () => {
		this.invalidate();
	}

	private _handleBuyClick = (e: Event) => {
		// Stop event propagation so the parent doesn't open the item pop
		e.stopPropagation();

		const item = this.item;
		this.analytics.trackEcomItem(item.id, item.merchantId, item.merchant, 1, false);
	}

	private getAlt = (item: EcomItem) => {
		return i18n("search_page_alt", {
			city: this.location.city,
			itemName: item.getName(),
		});
	}

	private getH3 = (item: EcomItem) => {
		const status = item.getExpired() ? i18n("expired") : i18n("online");
		return i18n("search_page_h3", {
			itemName: item.getName(),
			status
		});
	}
}

// DivContainer ---------------------------------------------->

@element("flipp-div-container")
class DivContainer extends Component<any, any> {
	render() {
		return <template>
			<content />
		</template>;
	}
}

// EcomItemDialogContent ---------------------------------------------->

interface EcomItemDialogContentAttributes extends JSX.HTMLProps {
	itemId: number;
	handleBuyClick: (e: Event) => void;
}

@element("flipp-ecom-item-dialog-content")
class EcomItemDialogContent extends Component<EcomItemDialogContentAttributes, EcomItem> {

	@attr itemId: number;
	@handler handleBuyClick: (e: Event) => void;

	@inject ecomService: EcomService;
	@inject analytics: AnalyticsService;
	@inject config: ConfigService;

	prepare() {
		return this.ecomService.getEcomItem(this.itemId);
	}

	renderLoading() {
		return <div class="loading-dialog-wrapper">
				<div class="loading" flex-horizontal>
					<div>
						<div class="spinner"></div>
					</div>
				</div>
			</div>;
	}

	render(ecomItem: EcomItem) {
		const ecomDisclaimer = (<div class="pop-disclaimer">{i18n("ecom_disclaimer")}</div>);

		const description = ecomItem.description ?
				<div>
					<h3>{i18n("description")}</h3>
					<span class="description-details">{ecomItem.description}</span>
				</div>
				: null;

		const sku = ecomItem.sku ?
				<div class="flyer-item-sku">SKU: {ecomItem.sku}</div> : null;

		const features = ecomItem.features && ecomItem.features.length > 0 ?
				<section class="features">
					<h3>{i18n("features")}</h3>
					<ul>
						{ecomItem.features.map((feature) => {
							return <li>{feature.text}</li>;
						})}
					</ul>
				</section> : null;

		const specs = ecomItem.specs && ecomItem.specs.length > 0 ?
				<section class="specs">
					<h3>{i18n("specs")}</h3>
					<ul>
						{ecomItem.specs.map((spec) => {
							return <li>
								<span class="spec-name">{spec.name}</span>
								<span class="spec-value">{spec.value}</span>
							</li>;
						})}
					</ul>
				</section> : null;

		const reviews = ecomItem.reviewSamples && ecomItem.reviewSamples.length > 0 ?
				<section>
					<h3>{i18n("reviews")}</h3>
					{/*<div class="review-summary">
						{i18n("overall_rating")}
						<flipp-rating value={item.reviewAverageRating.toString()}/>
					</div>*/}
					<ul>
						{ecomItem.reviewSamples.map((review) => {
							return <li>
								<div class="review">
									<div class="title-area">
										{review.rating ? <flipp-rating value={review.rating.toString()}/> : null}
										<div class="byline">
											<div class="title">{review.title}</div>
											<span class="author">{i18n("review_by_author", {author: review.author})}</span>
											{/* Make sure that 'review.date' is defined, because, otherwise, Jan 1 1970 is returned. */}
											{review.date && <span class="date">{ll(new Date(review.date))}</span>}
										</div>
									</div>
									<div class="body">{review.body}</div>
								</div>
							</li>;
						})}
					</ul>
				</section> : null;

		const item = this.model;
		const originalPriceBox = item.originalPrice && item.currentPrice !== item.originalPrice ?
			<div class="original-price-info">
				<flipp-price value={`${item.originalPrice}`}/>
				<span>{i18n("you_save")}</span>
				<flipp-price value={`${Number(item.originalPrice) - Number(item.currentPrice)}`}/>
				<span>({i18n("percent_off", {
					"percent": `${Math.floor(((Number(item.originalPrice) - Number(item.currentPrice)) /
						Number(item.originalPrice)) * 100)}`
				})})</span>
			</div> : null;

		return <ItemDialog details={{
				itemImageUrl: ecomUtil.thumbnail(this.model),
				merchantLogoUrl: this.model.merchantLogo,
				unavailable: null
			}}>
				<span content-slot="title">{this.model.name}</span>
				<span content-slot="subtitle">
					<div class="item-price-info">
						<div><flipp-price value={`${this.model.currentPrice || this.model.originalPrice}`} /></div>
					</div>
				</span>

				{originalPriceBox ?
					<span content-slot="dealDetails">
						<div>{originalPriceBox}</div>
					</span>
				: null}
				<div content-slot="cta">
					<div class="cta-buttons" flex-horizontal>
						<div flex-vertical>
							<a class="flipp-button" onClick={this._handleClick} href={item.url} target="_blank" rel="nofollow">
								<div>
									<span class="icon">
										<img class="buy-online-icon" src="https://f.wishabi.net/creative/Distribution/images/ecom/buy-online.svg" alt=""/>
									</span>
									<span class="label">{i18n("buy_online")}</span>
								</div>
							</a>
							<span class="at-merchant">{ecomItem.merchant ? i18n("at_merchant_name", { merchantName: ecomItem.merchant }) : null}</span>
						</div>
						<ClipEcomItemButton ecomItemId={this.model.id} />
					</div>
				</div>
				<div class="content-wrapper">
					<section class="description">
						{description}
						{sku}
					</section>
					{features}
					{specs}
					{reviews}
					{ecomDisclaimer}
				</div>
			</ItemDialog>;
	}

	private _handleClick = (e: Event) => {
		this.handleBuyClick(e);
	}
}
